import {
  ListRequest,
  useRltCertificateListQuery,
} from "@/services/backend/rlt/certificates/api/certificate-api";
import { rfc3339toDateString } from "@/shared/lib/utilities/date";
import { ReactNode } from "react";
import t from "@/lang/lang";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Certificate } from "@/services/backend/rlt/certificates/types/certificate";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";

export function CertificateListTable({
  request,
  onRequestChange,
  renderRowLink,
  hidePagination = false,
  hideBorder = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (certificateId: string, children: ReactNode) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  const { data: list, isLoading, error } = useRltCertificateListQuery(request);

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("Ausstellung")}</TableHead>
              <TableHead>{t("Arbeitsbeginn")}</TableHead>
              <TableHead className="w-full">{t("Arbeitsende")}</TableHead>
              <TableHead className="whitespace-nowrap">
                {t("Aktualisiert am")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={6} />}
            {error && <TableRtkErrorRow error={error} colSpan={6} />}
            {emptyList && (
              <TableEmptyRow
                colSpan={6}
                text={t("Keine Zertifikate gefunden")}
              />
            )}
            {list &&
              list.items.map((certificate) => (
                <CertificateListTableRow
                  key={certificate.id}
                  certificate={certificate}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

function CertificateListTableRow({
  certificate,
  renderRowLink,
}: {
  certificate: Certificate;
  renderRowLink: (certificateId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        {renderRowLink(
          certificate.id,
          <ReferenceNumber referenceNumber={certificate.referenceNumber} />,
        )}
      </TableCell>
      <TableCell>
        <DocumentStateBadge document={certificate} />
      </TableCell>
      <TableCell>{issueDate(certificate.issueDate)}</TableCell>
      <TableCell>{rfc3339toDateString(certificate.workStartedAt)}</TableCell>
      <TableCell>{rfc3339toDateString(certificate.workEndedAt)}</TableCell>
      <TableCell className="whitespace-nowrap">
        {rfc3339toDateString(certificate.updatedAt)}
      </TableCell>
    </TableRow>
  );
}

function issueDate(date: string): string {
  const dateString = rfc3339toDateString(date);
  if (dateString === "") {
    return "Nicht ausgestellt";
  }
  return dateString;
}

export function CertificateListTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <OrderBySelect<string>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Ausstellung"), value: "issueDate" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Aktualisiert am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}

export function useDefaultRltCertificateListRequest(
  processId: string,
): ListRequest {
  return {
    process: { active: true, values: [processId] },
    orderBy: [{ field: "createdAt" }],
    limit: 50,
    offset: 0,
  };
}
